import { MarkerClusterer } from '@googlemaps/markerclusterer';

import { Component, Element, Fragment, h, Listen, Prop, State } from '@stencil/core';
import { Producer, ProducerRaw } from '../../models/producer';
import {
  animatedZoom,
  getMarkerIcons,
  initMap,
  LANGUAGES,
  MAX_ZOOM,
  setMarkerIcon,
  transformProducer,
  transformProducers,
  updateMarkerAndCluster,
} from '../../utils';
import producerData from './../../data/producer-data.json';

@Component({
  tag: 'mgb-producer-map',
  styleUrl: 'mgb-producer-map.scss',
  shadow: true,
})
export class MgbProducerMap {
  @Element()
  host: HTMLMgbProducerMapElement;

  @Prop()
  language: keyof typeof LANGUAGES = 'de';

  @Prop()
  mobileWidth: boolean = false;

  @State()
  category: string;

  @State()
  selectedMarker: google.maps.Marker;

  @State()
  selectedCategories: string[] = [];

  @State()
  householdMarkerArray: google.maps.Marker[] = [];

  @State()
  clothingMarkerArray: google.maps.Marker[] = [];

  @State()
  stationeryMarkerArray: google.maps.Marker[] = [];

  @State()
  markerListeners: google.maps.MapsEventListener[] = [];

  @State()
  selectedProducer?: Producer;

  @State()
  selectedProducers: Producer[] = [];

  @State()
  map: google.maps.Map | null;

  @Listen('categorySelected', { target: 'body' })
  categorySelectedHandler(event: CustomEvent<{ category: string[] }>) {
    const { category } = event.detail;
    this.selectedCategories = category;

    updateMarkerAndCluster(
      this.map,
      this.selectedCategories,
      'household',
      this.householdMarkerArray,
      this.householdClusterer,
    );
    updateMarkerAndCluster(this.map, this.selectedCategories, 'clothing', this.clothingMarkerArray, this.clothingClusterer);
    updateMarkerAndCluster(
      this.map,
      this.selectedCategories,
      'stationery',
      this.stationeryMarkerArray,
      this.stationeryClusterer,
    );
  }

  @Listen('closeSingleProducerCard')
  closeSingleCardHandler() {
    this.selectedProducer = undefined;
  }

  @Listen('closeMultiProducerCard')
  closeMultiCardHandler() {
    this.selectedProducers = [];
  }

  private householdClusterer: MarkerClusterer;
  private clothingClusterer: MarkerClusterer;
  private stationeryClusterer: MarkerClusterer;

  private onClusterClick = (_event, cluster, map) => {
    const targetZoom = map.getZoom() + 3;
    if (targetZoom >= MAX_ZOOM) {
      const producerNames: string[] = cluster.markers.map(({ title }: { title: string }) => title);

      this.selectedProducers = transformProducers(
        producerData.filter((producerRaw: ProducerRaw) => producerNames.includes(producerRaw.producer)),
      );
    } else {
      animatedZoom(map as google.maps.Map, targetZoom, 250, cluster.position);
    }
  };

  async componentDidLoad() {
    const mapElement = this.host.shadowRoot?.getElementById('map');
    if (!mapElement) {
      return;
    }

    const { map, createMarker, householdCluster, clothingCluster, stationeryCluster } = await initMap(mapElement);

    if (!map) {
      return;
    }
    this.map = map;

    producerData.map((producerRaw: ProducerRaw) => {
      const producer = transformProducer(producerRaw);
      const { category } = producer;

      const marker = createMarker(producer);
      const { SCALED_SIZE } = getMarkerIcons(category);

      switch (category) {
        case 'household':
          this.householdMarkerArray.push(marker);
          break;
        case 'clothing':
          this.clothingMarkerArray.push(marker);
          break;
        case 'stationery':
          this.stationeryMarkerArray.push(marker);
          break;
        default:
          break;
      }

      this.markerListeners.push(
        marker.addListener('click', () => {
          //this sets the previously selected marker icon to the default marker icon
          if (this.selectedMarker !== undefined) {
            setMarkerIcon(this.selectedMarker, getMarkerIcons(this.category ?? '').DEFAULT_ICON_URL, SCALED_SIZE);
          }

          //This is setting this.category to category, which is likely the category of the clicked marker.
          this.category = category;
          // This sets the icon of the selected marker to the selected icon for its category
          setMarkerIcon(marker, getMarkerIcons(this.category).SELECTED_ICON_URL, SCALED_SIZE);
          //sets the selected marker to the currently selected marker
          this.selectedMarker = marker;

          this.selectedProducer = producer;
        }),
      );

      return marker;
    });

    this.householdClusterer = new MarkerClusterer({
      map,
      markers: this.householdMarkerArray,
      renderer: householdCluster(map),
      onClusterClick: this.onClusterClick,
    });
    this.clothingClusterer = new MarkerClusterer({
      map,
      markers: this.clothingMarkerArray,
      renderer: clothingCluster(map),
      onClusterClick: this.onClusterClick,
    });
    this.stationeryClusterer = new MarkerClusterer({
      map,
      markers: this.stationeryMarkerArray,
      renderer: stationeryCluster(map),
      onClusterClick: this.onClusterClick,
    });
  }

  disconnectedCallback() {
    if (this.markerListeners.length === 0) {
      return;
    }

    this.markerListeners.forEach((listener) => listener.remove());
    this.markerListeners = [];
  }

  componentWillRender() {
    const map = this.host.shadowRoot?.getElementById('map-container');
    this.mobileWidth = map?.clientWidth !== undefined && map.clientWidth < 768;
  }

  render() {
    return (
      <Fragment>
        <div class="map-container" id="map-container">
          <category-filter language={this.language} mobileWidth={this.mobileWidth}></category-filter>
          <div id="map" data-test-id="map"></div>
        </div>
        <single-producer-card language={this.language} producer={this.selectedProducer}></single-producer-card>
        <multi-producer-card language={this.language} producers={this.selectedProducers}></multi-producer-card>
      </Fragment>
    );
  }
}
